import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiService } from '../../services/apiService';
import type { Keyword, KeywordCheck } from '../../types/keywords';
import { formatShare, makeKeywordId } from './utils';

interface KeywordDialogProps {
  /** The keyword to edit; `null` creates a new one. */
  keyword: Keyword | null;
  existingIds: Set<string>;
  labelOf: (id: string) => string;
  onSave: (keyword: Keyword) => void;
  onClose: () => void;
}

function similarAboveThreshold(check: KeywordCheck) {
  return check.similar.filter((s) => s.similarity >= check.similarity_threshold);
}

function hasWarnings(check: KeywordCheck): boolean {
  return (
    similarAboveThreshold(check).length > 0 ||
    check.overlaps.length > 0 ||
    check.mentions.too_rare ||
    check.mentions.too_broad
  );
}

/**
 * Create or edit a keyword. Mount it with a `key` so it starts from fresh state.
 *
 * Before a new or changed keyword is saved, the backend checks it (similar
 * keywords, overlap with filters, mentions in documents). Warnings are shown
 * and must be confirmed with "Save anyway"; they never block saving.
 */
export function KeywordDialog({ keyword, existingIds, labelOf, onSave, onClose }: KeywordDialogProps) {
  const { t } = useTranslation('admin');
  const [labelEn, setLabelEn] = useState(keyword?.labels.en ?? '');
  const [labelDe, setLabelDe] = useState(keyword?.labels.de ?? '');
  const [definition, setDefinition] = useState(keyword?.definition ?? '');
  const [synonyms, setSynonyms] = useState(keyword?.synonyms.join(', ') ?? '');
  const [check, setCheck] = useState<KeywordCheck | null>(null);
  const [checking, setChecking] = useState(false);

  // The ID is derived once on creation and never changes afterwards.
  const id = keyword?.id ?? makeKeywordId(labelEn, existingIds);

  // Any edit invalidates the shown warnings.
  const edit = (setter: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setCheck(null);
  };

  const build = (): Keyword => {
    const labels: Record<string, string> = { ...keyword?.labels, en: labelEn.trim() };
    if (labelDe.trim()) labels.de = labelDe.trim();
    else delete labels.de;
    return {
      id,
      labels,
      definition: definition.trim(),
      synonyms: synonyms.split(',').map((s) => s.trim()).filter(Boolean),
      origin: keyword?.origin ?? 'manual',
      created_at: keyword?.created_at,
      reevaluate: keyword?.reevaluate ?? false,
    };
  };

  const contentChanged = (next: Keyword) =>
    !keyword ||
    JSON.stringify([next.labels, next.definition, next.synonyms]) !==
      JSON.stringify([keyword.labels, keyword.definition, keyword.synonyms]);

  const handleSave = async () => {
    const next = build();
    // A second click after the warnings confirms them.
    if (check || !contentChanged(next)) {
      onSave(next);
      return;
    }
    setChecking(true);
    try {
      const result = await apiService.checkKeyword(next);
      if (hasWarnings(result)) setCheck(result);
      else onSave(next);
    } catch {
      onSave(next); // the check is advisory; it must not prevent saving
    } finally {
      setChecking(false);
    }
  };

  const mentions = check?.mentions;
  const mentionValues = mentions && { count: mentions.documents, share: formatShare(mentions.share) };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{keyword ? t('keywords.editTitle') : t('keywords.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('keywords.labelEn')}
            value={labelEn}
            onChange={edit(setLabelEn)}
            required
            autoFocus
            helperText={t('keywords.idHint', { id: id || '–' })}
          />
          <TextField label={t('keywords.labelDe')} value={labelDe} onChange={edit(setLabelDe)} />
          <TextField
            label={t('keywords.definition')}
            value={definition}
            onChange={edit(setDefinition)}
            helperText={t('keywords.definitionHint')}
            multiline
            minRows={2}
          />
          <TextField
            label={t('keywords.synonyms')}
            value={synonyms}
            onChange={edit(setSynonyms)}
            helperText={t('keywords.synonymsHint')}
          />
          {check && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={500}>{t('check.title')}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {similarAboveThreshold(check).map((s) => (
                  <li key={s.keyword_id}>
                    {t('check.similar', { label: labelOf(s.keyword_id), similarity: s.similarity.toFixed(2) })}
                  </li>
                ))}
                {check.overlaps.map((o) => (
                  <li key={`${o.text}-${o.reason}`}>"{o.text}" – {t(`review.overlap.${o.reason}`)}</li>
                ))}
                {mentions?.too_rare && <li>{t('check.rare', mentionValues)}</li>}
                {mentions?.too_broad && <li>{t('check.broad', mentionValues)}</li>}
              </Box>
              {!check.similarity_checked && (
                <Typography variant="caption">{t('check.similarityUnavailable')}</Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!labelEn.trim() || !id || checking}>
          {check ? t('check.saveAnyway') : t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
